import { Box, Chip, Collapse, IconButton, Stack, Typography } from '@mui/material';
import {
  parseEscalationRules,
  PolicyEvaluationContext,
  TimeWindow,
} from '@provision/sidekick-utils';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import React, { useEffect, useRef } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { EscalationCard } from './escalation-card';
import {
  CardContent,
  CardRail,
  CardRoot,
  chevronSx,
  Description,
  Dot,
  getCardFocusSx,
  getValidityChipSx,
  KebabSlot,
  Meta,
  PolicyName,
  RowHead,
  TagRow,
} from './policy-card.styles';
import { getPolicyColor } from './policy-color';
import {
  POLICY_CATEGORY_ESCALATION,
  TYPE_FRIENDLY_NAME_POLICIES_MAP,
} from './policy-constants';

interface PolicyCardProps {
  policy: NormalizedSupportPolicy;
  partnerId: number;
  expanded: boolean;
  hasAccessToManageSupportPolicies: boolean;
  onExpandClick: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  evaluationServiceLevel: string;
  currentTime: Date;
  policyEvaluationContext: PolicyEvaluationContext;
  isPreLits?: boolean;
  getEscalationCutoffHoursForUrgency?: (urgencyLevel: string) => TimeWindow[];
  isFocused?: boolean;
  onFocusHandled?: () => void;
}

export const PolicyCard = ({
  policy,
  partnerId,
  expanded,
  hasAccessToManageSupportPolicies,
  onExpandClick,
  onMenuOpen,
  evaluationServiceLevel,
  currentTime,
  policyEvaluationContext,
  isPreLits,
  getEscalationCutoffHoursForUrgency,
  isFocused,
  onFocusHandled,
}: PolicyCardProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    // centred before the glow starts, so the card does not appear and then jump
    rootRef.current?.scrollIntoView({ block: 'center' });
  }, [isFocused]);

  const color = getPolicyColor(policy);
  const isValidFuture = !policy.validUntil || dayjs(policy.validUntil).isAfter(dayjs());
  const canManage =
    hasAccessToManageSupportPolicies &&
    (policy.partnerId !== PLATFORM_PARTNER_ID || partnerId === PLATFORM_PARTNER_ID);
  const createdAtDisplay = dayjs(policy.createdAt).format('MMM D, YYYY h:mm A');

  const escalationCutoffHours =
    getEscalationCutoffHoursForUrgency && policy.urgencyLevel
      ? getEscalationCutoffHoursForUrgency(policy.urgencyLevel)
      : policyEvaluationContext.escalationCutoffHours;

  return (
    <CardRoot
      ref={rootRef}
      data-testid="policy-card"
      data-policy-id={policy.id}
      onAnimationEnd={isFocused ? onFocusHandled : undefined}
      sx={[{ backgroundColor: `${color}20` }, isFocused ? getCardFocusSx : false]}
    >
      <CardRail sx={{ backgroundColor: color }} />

      <CardContent>
        <RowHead onClick={onExpandClick}>
          <Dot sx={{ backgroundColor: color }} />
          <PolicyName variant="body2">{policy.title}</PolicyName>

          {policy.pinnedAt ? (
            <DynamicIcon name="push_pin" sx={{ fontSize: '1rem', flexShrink: 0 }} />
          ) : null}
          {policy.archivedAt ? (
            <DynamicIcon name="archive" sx={{ fontSize: '1rem', flexShrink: 0 }} />
          ) : null}
          {!isValidFuture && (
            <DynamicIcon name="timer_off" sx={{ fontSize: '1rem', flexShrink: 0 }} />
          )}

          <DynamicIcon name="expand_more" sx={chevronSx(expanded)} />

          <KebabSlot>
            {canManage ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMenuOpen(e);
                }}
              >
                <DynamicIcon name="more_vert" />
              </IconButton>
            ) : null}
          </KebabSlot>
        </RowHead>

        {policy.category === POLICY_CATEGORY_ESCALATION ? (
          <EscalationCard
            rules={parseEscalationRules(policy.escalationRules)}
            urgencyLevel={policy.urgencyLevel}
            serviceLevel={evaluationServiceLevel}
            currentTime={currentTime}
            timezone={policyEvaluationContext.timezone}
            businessHours={policyEvaluationContext.businessHours}
            escalationCutoffHours={escalationCutoffHours}
            holidays={policyEvaluationContext.holidays}
            expanded={expanded}
            isPreLits={isPreLits}
          />
        ) : null}

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box mt={1}>
            {/* Hide the description for escalation policies - it contains auto-generated
                response rules which are shown in a cleaner format in EscalationCard */}
            {policy.category !== POLICY_CATEGORY_ESCALATION && policy.description ? (
              // partner-authored rich text; DOMPurify strips scripts and handlers first
              <Description
                data-testid="policy-card-description"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(policy.description, {
                    USE_PROFILES: { html: true },
                  }),
                }}
              />
            ) : null}

            <Stack direction="row" alignItems="center" gap={0.625} flexWrap="wrap" mt={1.25}>
              <TagRow>
                <DynamicIcon name="label" sx={{ fontSize: '1rem', color: 'inherit' }} />
                {TYPE_FRIENDLY_NAME_POLICIES_MAP[policy.type] || policy.type}
              </TagRow>

              {policy.validUntil ? (
                <>
                  <Typography
                    variant="caption"
                    sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant }}
                  >
                    &middot;
                  </Typography>
                  <Chip
                    label={
                      isValidFuture
                        ? `Valid until ${dayjs(policy.validUntil).format('MMM D, YYYY')}`
                        : `Expired at ${dayjs(policy.validUntil).format('MMM D, YYYY')}`
                    }
                    size="small"
                    variant="outlined"
                    sx={getValidityChipSx(isValidFuture)}
                  />
                </>
              ) : null}
            </Stack>

            <Meta>
              {`Created by ${policy.createdByUser?.firstName} ${policy.createdByUser?.lastName} at ${createdAtDisplay}`}
            </Meta>
          </Box>
        </Collapse>
      </CardContent>
    </CardRoot>
  );
};
